import * as React from 'react';
import { Button, Container, Flashbar, FormField, Header, Input, Multiselect, RadioGroup, Autosuggest, Icon, SpaceBetween, Toggle, Box, Alert, Table, ProgressBar } from '@amzn/awsui-components-react-v3';
import { BreadcrumbGroup, ColumnLayout } from '@amzn/awsui-components-react-v3'
import { EmployeeSuggestions } from '../components/shared/EmployeeSuggestions';
import AdditionalContacts from '../components/shared/AdditionalContacts'
import useItemCreate from './ItemCreate.useItemCreate'
import { Redirect, useHistory } from "react-router-dom";
import { ValidationField } from '../models/item-detail-model';
import { cetMultiOptions, militaryUseOptions, researchProjectOptions, highlyConfidentialOptions, cetNotApplicableOption, isCNCOptions, isDesignManufacturingOptions, isEnterpriseManagementOptions } from "./../components/Itemv2/questionOption.constants";
import { getSaasDefinition } from '../components/shared/SaaSRestrictionDefinition';
import { SERVICE_ID_DESCRIPTION } from '../components/shared/ServiceIdDescription';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../reducers';
import { fetchItems } from '../reducers/ItemsSlice';
import { submitItem } from '../actions/async.action';
import { Item } from '../models/item-model';
import { MAX_BATCH_COMPONENTS, ITEM_TYPE_OPTIONS } from '../models/componentCreation.constants';
import {
  BatchComponentItem,
  BatchProgress,
  BatchError,
  CreatedItem,
  ParentItemData,
} from '../models/batch-component-model';
import {
  createEmptyComponent,
  createComponentsBatch,
  areAllComponentsValid,
  extractErrorMessage,
} from '../utils/batchComponentCreation.utils';

const ItemCreate = () => {
  const {
    errorMessages,
    itemId,
    setErrorMessages,
    itemInfo,
    onItemInfoChange: onChange,
    onNewSubmit: onSubmit,
    bcItemCreate
  } = useItemCreate()

  const history = useHistory();
  const dispatch = useDispatch();
  
  // Get lab settings to check if parent/component feature is enabled
  const labSettings = useSelector((state: RootState) => state.labSettingsObj);
  const isParentComponentFeatureEnabled = labSettings?.parentComponentItems ?? false;
  
  // Batch component creation state
  const [batchComponents, setBatchComponents] = React.useState<BatchComponentItem[]>([]);
  const [isBatchSubmitting, setIsBatchSubmitting] = React.useState(false);
  const [batchProgress, setBatchProgress] = React.useState<BatchProgress>({ current: 0, total: 0, message: '' });
  const [batchErrors, setBatchErrors] = React.useState<BatchError[]>([]);
  const [batchCreatedItems, setBatchCreatedItems] = React.useState<CreatedItem[]>([]);
  
  // State for item auto-suggestion
  const [filteredItems, setFilteredItems] = React.useState([]);
  

  // Get items from Redux store with strong typing
  const itemsState = useSelector((state: RootState) => state.itemsObj);
  const { items, status, error } = itemsState;

  // Ensure items are loaded when component mounts
  React.useEffect(() => {
    // If items haven't been loaded yet, trigger the fetch
    if (status === 'idle') {
      dispatch(fetchItems());
    }
  }, [dispatch, status]);
  

  // Add a new component to the batch list
  const addBatchComponent = () => {
    if (batchComponents.length >= MAX_BATCH_COMPONENTS) return;
    setBatchComponents([...batchComponents, createEmptyComponent()]);
  };

  // Remove a component from the batch list
  const removeBatchComponent = (id: string) => {
    setBatchComponents(batchComponents.filter(c => c.id !== id));
  };

  // Update a component in the batch list
  const updateBatchComponent = (id: string, field: keyof BatchComponentItem, value: string) => {
    setBatchComponents(batchComponents.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    ));
  };

  // Handle batch submission: create parent first, then components
  const handleBatchSubmit = async () => {
    setIsBatchSubmitting(true);
    setBatchErrors([]);
    setBatchCreatedItems([]);
    
    const total = 1 + batchComponents.length; // parent + components
    setBatchProgress({ current: 0, total, message: 'Creating Service Item...' });

    try {
      // First, create the parent item
      const parentData = { ...itemInfo };
      delete (parentData as any).QA;
      
      const parentResponse = await submitItem(parentData);
      const parentId = parentResponse.data.Id;
      
      setBatchProgress({ current: 1, total, message: 'Service Item created successfully' });
      setBatchCreatedItems([{ name: itemInfo.ItemFullName, id: parentId }]);
      
      // Now create components using shared utility
      const parentItemData: ParentItemData = {
        BusinessLineAttorney: itemInfo.BusinessLineAttorney,
        BusinessLineAttorneyName: itemInfo.BusinessLineAttorneyName,
        OtherContributors: itemInfo.OtherContributors,
        Description: itemInfo.Description,
        OnlineDocumentation: itemInfo.OnlineDocumentation,
        MilitaryUse: itemInfo.MilitaryUse,
        ResearchProject: itemInfo.ResearchProject,
        CET: itemInfo.CET,
        Confidential: itemInfo.Confidential,
        SaaSRestrictions: itemInfo.SaaSRestrictions,
      };

      const { createdItems, errors } = await createComponentsBatch(
        batchComponents,
        parentId,
        parentItemData,
        (progress) => {
          setBatchProgress({
            current: progress.current + 1, // +1 to account for parent
            total,
            message: progress.message,
          });
        }
      );

      // Update state with results
      setBatchCreatedItems((prev) => [...prev, ...createdItems]);
      setBatchErrors(errors);
      
      // Refresh items list
      dispatch(fetchItems());
      
      // If no errors, redirect to parent item after a brief delay
      if (errors.length === 0) {
        setTimeout(() => {
          history.push(`/items/${parentId}`);
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to create parent item:', err);
      const errorMessage = extractErrorMessage(err);
      setBatchErrors([{ componentName: 'Service Item', error: errorMessage }]);
      setIsBatchSubmitting(false);
    }
  };

  // Determine status type for Autosuggest component (for item name search)
  const getStatusType = () => {
    if (status === 'idle') return 'loading';
    if (status === 'loading') return 'loading';
    if (status === 'failed') return 'error';
    return 'finished';
  };

  React.useEffect(() => {
    if (items && itemInfo.ItemFullName) {
      const searchValue = itemInfo.ItemFullName.toLowerCase();
      // Existing items may have missing values, so every field access must be null-safe
      setFilteredItems(items.filter((item) => {
        return item?.ItemFullName?.toLowerCase().includes(searchValue)
          || item?.CodeName?.toLowerCase().includes(searchValue)
          || item?.Pm?.toLowerCase().includes(searchValue)
          || item?.BusinessLineAttorney?.toLowerCase().includes(searchValue)
          || item?.OtherContributors?.some((contact) => contact?.Alias?.toLowerCase().includes(searchValue));
      }));
    } else {
      setFilteredItems([]);
    }
  }, [itemInfo.ItemFullName, items]);

  const getErrorMessage = (field: string): string => {
    if (!errorMessages || errorMessages.length === 0) {
      return '';
    }

    for (const message of errorMessages) {
      if (message.field === field) {
        return message.message
      }
    }
    return ''
  }

  const dissmissErrorMessage = (field: string) => {
    if (!errorMessages || errorMessages.length === 0) {
      return;
    }

    const deepCopyErrorMessages = JSON.parse(JSON.stringify(errorMessages))
    for (const message of deepCopyErrorMessages) {
      if (message.field === field) {
        message.message = ''
      }
    }
    setErrorMessages(deepCopyErrorMessages)
  }

  const itemIdentification = <Container header="Item Identification">
    <ColumnLayout>
      <ColumnLayout columns={1}>
        <FormField id="Item_Full_Name" label={<div><span>Item Full Name</span></div>}
            description={<>
              <p>The name of the product, service, or technology. If the Item has not yet been officially named, use the internal code name</p>
              <SpaceBetween size="xxs" direction="horizontal">
                <Icon name="status-warning" />
                <span>Features that are included in an existing service do not require their own CRIPT form. If it is not available independently, please review the existing CRIPT entry for the service and confirm the new feature capabilities are captured.</span>
              </SpaceBetween>
            </>}
            errorText={getErrorMessage(ValidationField.ItemFullName)}
        >
          <Autosuggest
            placeholder="Enter item name to search for existing items"
            empty="No existing items found"
            statusType={getStatusType()}
            errorText="Error fetching existing items, try again"
            loadingText="Searching existing items..."
            options={filteredItems.map((item) => {
              return { value: item.ItemFullName + ' (' + item.CodeName + ')' }
            })}
            value={itemInfo.ItemFullName}
            enteredTextLabel={() => itemInfo.ItemFullName}
            filteringType="manual"
            onChange={(event) => {
              onChange({ ItemFullName: event.detail.value })
              dissmissErrorMessage(ValidationField.ItemFullName)
            }}
            onSelect={(event) => {
              // Extract the CodeName from the selected option to find the item ID
              // The "entered text" option holds only the typed name (no " (code)" suffix), so it may not match
              const codeNameMatch = event.detail.value.match(/ \((.*)\)$/);
              if (!codeNameMatch) {
                return;
              }
              const codeNameFromDisplay = codeNameMatch[1];
              let selectedItem = items.find(item => {
                return item.CodeName === codeNameFromDisplay;
              });
              
              if (selectedItem) {
                // Navigate to the existing item's detail page
                history.push(`/items/${selectedItem.Id}`);
              }
            }}
          />
        </FormField>
        <FormField id="Internal_Code_Name" label={<div><span>Internal Code Name</span></div>}
          description="The code name used internally for the product, service, or technology"
          errorText={getErrorMessage(ValidationField.CodeName)}
        >
          <Input
            value={itemInfo.CodeName}
            onChange={event => {
                onChange({ CodeName: event.detail.value })
                dissmissErrorMessage(ValidationField.CodeName)

            }}
          />
        </FormField>
      </ColumnLayout>
      <ColumnLayout columns={1}>
        <FormField id="version" label={<div><span>Item version<i> - optional</i></span></div>}
            description="The current version of the product, service, or technology"
        >
          <Input
              value={itemInfo.CustomerDefinedVersion}
              onChange={(event) => {
                  onChange({ CustomerDefinedVersion: event.detail.value })

              }}
          ></Input>
        </FormField>
        <FormField id="product_id" label={<div><span>Service ID<i> - optional</i></span></div>}
            description={SERVICE_ID_DESCRIPTION}
        >
          <Input
              value={itemInfo.ProductId || ""}
              onChange={(event) => {
                  onChange({ ProductId: event.detail.value })
              }}
              placeholder="Enter service ID"
          ></Input>
        </FormField>
        <FormField id="highly_confidential" label={<div><span>Highly Confidential</span></div>}
          description={<span>Clicking "yes" will limit access to this record to employees in this <a href="https://permissions.amazon.com/a/team/cript-leadership" target="_blank">permission group</a>, as well as any users specified in the "Product Manager", "Business Line Lawyer", and "Additional Contributors" fields.</span>}
        >
          <RadioGroup
            items={highlyConfidentialOptions}
            value={itemInfo.Confidential}
            onChange={(event) => {
              onChange({ Confidential: event.detail.value })

            }}
          ></RadioGroup>
        </FormField>
      </ColumnLayout>
    </ColumnLayout>
  </Container>


  // Batch submission progress UI
  const batchProgressSection = isBatchSubmitting && (
    <Container header={<Header variant="h2">Creating Items...</Header>}>
      <SpaceBetween size="l">
        <ProgressBar
          value={(batchProgress.current / batchProgress.total) * 100}
          label="Creation Progress"
          description={batchProgress.message}
          additionalInfo={`${batchProgress.current} of ${batchProgress.total} items processed`}
        />
        
        {batchCreatedItems.length > 0 && (
          <Box>
            <Header variant="h3">Successfully Created:</Header>
            <ul>
              {batchCreatedItems.map((item, idx) => (
                <li key={idx}>
                  <Icon name="status-positive" variant="success" /> {item.name}
                </li>
              ))}
            </ul>
          </Box>
        )}
        
        {batchErrors.length > 0 && (
          <Alert type="error" header="Some items failed to create:">
            <ul>
              {batchErrors.map((err, idx) => (
                <li key={idx}><strong>{err.componentName}:</strong> {err.error}</li>
              ))}
            </ul>
          </Alert>
        )}
        
        {batchProgress.current === batchProgress.total && batchErrors.length === 0 && (
          <Alert type="success">
            All items created successfully! Redirecting to Service Item...
          </Alert>
        )}
        
        {batchProgress.current === batchProgress.total && batchErrors.length > 0 && (
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => history.push(`/items/${batchCreatedItems[0]?.id}`)}>
              View Service Item
            </Button>
            <Button variant="link" onClick={() => setIsBatchSubmitting(false)}>
              Close
            </Button>
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Container>
  );

  const parentComponentRelationship = isParentComponentFeatureEnabled && <Container header="Service/Related Item Relationship">
    <ColumnLayout columns={1}>
      <FormField id="is_parent" 
        label={<div><span>This Item is a Service</span></div>}
        description="Enable this if this Item can have Related Items. Service Items will display a 'Related Items' tab showing all linked child Items."
      >
        <Toggle
          checked={itemInfo.isParent}
          onChange={(event) => {
            onChange({ isParent: event.detail.checked })
            // Clear batch components if parent is toggled off
            if (!event.detail.checked) {
              setBatchComponents([]);
            }
          }}
        >
          {itemInfo.isParent ? 'Yes, this is a Service' : 'No'}
        </Toggle>
      </FormField>

      {itemInfo.isParent && (
        <FormField
          id="batch_components"
          label={
            <SpaceBetween direction="horizontal" size="xs">
              <span>Batch Create Related Items <i>- optional</i></span>
            </SpaceBetween>
          }
          description="Add Related Items that will be created along with this Service Item. Each Related Item will inherit most fields from the Service."
        >
          <SpaceBetween size="m">
            <Box>
              <Button
                onClick={addBatchComponent}
                disabled={batchComponents.length >= MAX_BATCH_COMPONENTS}
                iconName="add-plus"
              >
                Add Related Item
              </Button>
            </Box>
            
            {batchComponents.length === 0 ? (
              <Box textAlign="center" padding="m" color="text-body-secondary">
                <SpaceBetween size="s">
                  <Icon name="status-info" size="medium" />
                  <div>No Related Items added yet. Click "Add Related Item" to create Related Items along with this Service Item.</div>
                </SpaceBetween>
              </Box>
            ) : (
              <SpaceBetween size="m">
                <Table
                  columnDefinitions={[
                    {
                      id: 'itemFullName',
                      header: 'Item Full Name',
                      cell: (item: BatchComponentItem) => (
                        <Input
                          value={item.ItemFullName}
                          placeholder="Enter Related Item name"
                          onChange={({ detail }) => updateBatchComponent(item.id, 'ItemFullName', detail.value)}
                        />
                      ),
                      width: 200
                    },
                    {
                      id: 'codeName',
                      header: 'Internal Code Name',
                      cell: (item: BatchComponentItem) => (
                        <Input
                          value={item.CodeName}
                          placeholder="Enter code name"
                          onChange={({ detail }) => updateBatchComponent(item.id, 'CodeName', detail.value)}
                        />
                      ),
                      width: 200
                    },
                    {
                      id: 'pm',
                      header: 'Product Manager',
                      cell: (item: BatchComponentItem) => (
                        <EmployeeSuggestions
                          value={item.Pm}
                          onSelect={(selected) => {
                            setBatchComponents(batchComponents.map(c => 
                              c.id === item.id 
                                ? { ...c, Pm: selected.alias, PmName: selected.name } 
                                : c
                            ));
                          }}
                        />
                      ),
                      width: 200
                    },
                    {
                      id: 'itemType',
                      header: 'Item Type',
                      cell: (item: BatchComponentItem) => (
                        <FormField>
                          <RadioGroup
                            items={ITEM_TYPE_OPTIONS}
                            value={item.ItemType}
                            onChange={({ detail }) => updateBatchComponent(item.id, 'ItemType', detail.value)}
                          />
                        </FormField>
                      ),
                      width: 250
                    },
                    {
                      id: 'actions',
                      header: 'Actions',
                      cell: (item: BatchComponentItem) => (
                        <Button
                          variant="icon"
                          iconName="remove"
                          onClick={() => removeBatchComponent(item.id)}
                          ariaLabel="Remove Related Item"
                        />
                      ),
                      width: 80
                    }
                  ]}
                  items={batchComponents}
                  empty={null}
                  variant="embedded"
                />
              </SpaceBetween>
            )}
          </SpaceBetween>
        </FormField>
      )}
      
    </ColumnLayout>
  </Container>

  const contacts = <Container header="Contacts">
    <ColumnLayout columns={1}>
        <FormField id="product_manager" label={<div><span>Product Manager</span></div>}
            errorText={getErrorMessage(ValidationField.Pm)}
        >
          <EmployeeSuggestions
            key={itemInfo.Pm}
            onSelect={selected => {
              onChange({ Pm: selected.alias, PmName: selected.name })
              dissmissErrorMessage(ValidationField.Pm)
            }}
            value={itemInfo.Pm}
          />
        </FormField>
        <FormField id="business_lawyer" label={<div><span>Business Line Lawyer</span></div>}
          errorText={getErrorMessage(ValidationField.BusinessLineAttorney)}
        >
          <EmployeeSuggestions
            key={itemInfo.BusinessLineAttorney}
            onSelect={(selected) => {
              {
                  onChange({ BusinessLineAttorney: selected.alias, BusinessLineAttorneyName: selected.name })
                  dissmissErrorMessage(ValidationField.BusinessLineAttorney)
              }
            }}
            value={itemInfo.BusinessLineAttorney}
          />
        </FormField>
        <FormField id="additional_contributor" label={<div><span>Additional Contributors<i> - optional</i></span></div>}
          description=' In addition to the Product Manager and Business Line Lawyer for this Item, the following individuals can view all Item details and perform any actions on this Item. '
        >
          <ColumnLayout>
            <AdditionalContacts
              items={itemInfo.OtherContributors}
              customOnChange={(value) => {
                onChange({ OtherContributors: value })
              }}
            />
          </ColumnLayout>
        </FormField>
    </ColumnLayout>
  </Container>

  const details =
    <Container header='Details'>
      <ColumnLayout columns={1}>
        <FormField id="description" label={<div><span>Description</span></div>}
          description='Provide a description of the Item, including what you understand to be the primary use/function.'
          errorText={getErrorMessage(ValidationField.Description)}
        >
          <Input
              value={itemInfo.Description}
              onChange={event => {
                  onChange({ Description: event.detail.value })
                  dissmissErrorMessage(ValidationField.Description)
              }
              }
          />
        </FormField>
        <FormField
          id="online_documentation" 
          label={<div><span>Online Documentation<i> - optional</i></span></div>}
          description="Provide a link to the item's online information"
        >
          <Input
            value={itemInfo.OnlineDocumentation}
            onChange={event => {
              onChange({ OnlineDocumentation: event.detail.value })
            }}
          />
        </FormField>
        <FormField id="military_use" label={<div><span>Military Use</span></div>}
          description={<div>
              Is either of the following true?
              <ol>
                  <li>The Item is designed specifically or primarily for military use, or</li>
                  <li>Likely customers for this Item are primarily military or defense organizations even if the Item is also intended for broader commercial use.</li>
              </ol>
          </div>}
          errorText={getErrorMessage(ValidationField.MilitaryUse)}
        >
          <RadioGroup items={militaryUseOptions}
            value={itemInfo.MilitaryUse}
            onChange={(event) => {
              onChange({ MilitaryUse: event.detail.value })
              dissmissErrorMessage(ValidationField.MilitaryUse)
            }}
          />
        </FormField>

        <FormField id="research_project" 
          label={<div><span>Research Project</span></div>}
          description={<div>Is the Item part of a basic or applied research project intended to be published and shared broadly within the scientific comunity?<br />
              Answers will generally be "No", but may be "Yes". if e.g., you're working with a university on your Item and the results of that work
              will be published in academic journals or elsewhere in the research community.
            </div>}
          errorText={getErrorMessage(ValidationField.ResearchProject)}
        >
          <RadioGroup items={researchProjectOptions}
            value={itemInfo.ResearchProject}
            onChange={(event) => {
                onChange({ ResearchProject: event.detail.value })
                dissmissErrorMessage(ValidationField.ResearchProject)
            }}
          />
        </FormField>
        <FormField id="cet"
          label={<div><span>Please select which category best fits your Item</span></div>}
          errorText={getErrorMessage(ValidationField.CET)}
        >
          <Multiselect
            options={cetMultiOptions.map((o) => { return {value: o.value, label: o.value, description: o.description}})}
            selectedOptions={itemInfo.CET.map((v) => { 
              return {value: v, label: v }
            })}
            onChange={event => {
              let selected = event.detail.selectedOptions.map(o => o.value)
              if (selected[selected.length - 1] === cetNotApplicableOption) {
                onChange({ CET: [cetNotApplicableOption] })
              } else if (selected.includes(cetNotApplicableOption)) {
                let updatedCET = selected.filter(id => id !== cetNotApplicableOption)
                onChange({ CET: updatedCET })
              } else {
                onChange({ CET: selected})
              }
            }}
          />
        </FormField>

        <FormField id="is_enterprise_management_software" label={<div>
              Is your Item one of the below types of Enterprise Management Software?? Please select yes if your item is a Software-as-a-Service product that has all the features of and/or is marketed as one of the below types of software. Applicable examples include, but are not limited to, RedShift (EDW),  QuickSight (BI), and AWS Supply Chain (SCM). 
              <ol type="a">
                <li>{getSaasDefinition("Enterprise Resource Planning")} (ERP)</li>
                <li>{getSaasDefinition("Customer Relationship Management")} (CRM)</li>
                <li>{getSaasDefinition("Business Intelligence")} (BI)</li>
                <li>{getSaasDefinition("Supply Chain Management")} (SCM)</li>
                <li>{getSaasDefinition("Enterprise Data Warehouse")} (EDW)</li>
                <li>{getSaasDefinition("Computerized Maintenance Management System")} (CMMS)</li>
                <li>{getSaasDefinition("Project management software")}</li>
                <li>{getSaasDefinition("Product Lifecycle Management")} (PLM)</li>
              </ol>
            </div>}
            errorText={getErrorMessage(ValidationField.SaaSRestrictions)}
          >
            <RadioGroup items={isEnterpriseManagementOptions}
              value={itemInfo.SaaSRestrictions.isEnterpriseManagementSoftware}
              onChange={(event) => {
                onChange({ SaaSRestrictions: {...itemInfo.SaaSRestrictions, isEnterpriseManagementSoftware: event.detail.value }})
              }} />
          </FormField>
          <FormField id="is_design_and_manufacturing_software" label={<div>
              Is this Item Design and Manufacturing Software used for architecture, engineering, construction, manufacturing, media, education, and entertainment? Please select yes if your item is a SaaS product that has all the features of and/or is marketed as one of the following types of software:
              <ol type="a">
                <li>{getSaasDefinition("Building Information Modeling")} (BIM)</li>
                <li>{getSaasDefinition("Computer Aided Design")} (CAD)</li>
                <li>{getSaasDefinition("Computer-Aided Manufacturing")} (CAM)</li>
                <li>{getSaasDefinition("Engineer to Order")} (ETO)</li>
              </ol>
            </div>}
            errorText={getErrorMessage(ValidationField.SaaSRestrictions)}
          >

            <RadioGroup items={isDesignManufacturingOptions}
              value={itemInfo.SaaSRestrictions.isDesignAndManufacturingSoftware}
              onChange={(event) => {
                onChange({ SaaSRestrictions: {...itemInfo.SaaSRestrictions, isDesignAndManufacturingSoftware: event.detail.value }})
              }} />
          </FormField>
          <FormField id="is_cnc_software" label={<div>
            Is this Item software for the {getSaasDefinition("operation")} of computer numerical control (CNC) machine tools?
            </div>}
            errorText={getErrorMessage(ValidationField.SaaSRestrictions)}
          >
            <RadioGroup items={isCNCOptions}
              value={itemInfo.SaaSRestrictions.isCNCSoftware}
              onChange={(event) => {
                onChange({ SaaSRestrictions: {...itemInfo.SaaSRestrictions, isCNCSoftware: event.detail.value }})
              }} />
          </FormField>
      </ColumnLayout>
    </Container>

  // Determine if batch submit should be used
  const shouldUseBatchSubmit = isParentComponentFeatureEnabled && itemInfo.isParent && batchComponents.length > 0;

  if (itemId) {
    return <Redirect to={{ pathname: `${itemId}`, state: { isNew: true } }} />
  }

  return (
    <div style={{ padding: '2rem' }} >
      <BreadcrumbGroup items={bcItemCreate}/>
      <h1 className='cript-page-header'>Create Item</h1>
      {errorMessages.length > 0 && (
        <div id="error-flash" className="CRIPT-notification">
          <Flashbar items={[{type: "error", content: errorMessages.find((e) => e.field === 'banner')?.message
            || "Submit failed. Review the error(s) in the form above and try again."}]} />
        </div>
      )}
      <SpaceBetween size="l">
        {isBatchSubmitting ? (
          batchProgressSection
        ) : (
          <>
            {itemIdentification}
            {parentComponentRelationship}
            {contacts}
            {details}
            <Button
              variant="primary"
              onClick={() => {
                if (shouldUseBatchSubmit) {
                  handleBatchSubmit();
                } else {
                  onSubmit();
                }
              }}
            >
              {shouldUseBatchSubmit ? `Submit (${1 + batchComponents.length} items)` : 'Submit'}
            </Button>
          </>
        )}
      </SpaceBetween>
    </div >
  )
}

export default ItemCreate
